import type { GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import { pixelsToCoords } from '../label.creator';
import type { ElementCreatorContext } from '../core/board.types';
import { clampToViewport, pickPositionFromSlope } from './label.auto-position';
import type { LabelContext, LabelPosition, ResolvedLabelConfig } from './label.model';
import { LABEL_POSITIONS } from './label.model';
import { convertLineBreaks, formatLabelText, renderLatex } from './label.formatters';
import { getAnchor, getCanvasBaseCoords, getCombinedOffset, isCanvasPosition } from './label.position';
import { getLabelStyle } from './label.utils';

/**
 * Minimum pixel margin between any label edge and the container boundary.
 * The label is nudged inward whenever its rendered bounding box breaches this margin.
 */
const VIEWPORT_INSET_PX = 6;

/**
 * Options for binding a label to an element.
 */
export interface BindLabelOptions {
  ctx: ElementCreatorContext;
  themeService: JsxgraphThemeService;
  element: GeometryElement;
  config: ResolvedLabelConfig;
  /** Provides geometric context for built-in placeholders ({x}, {y}, {name}, etc.). Optional for standalone labels. */
  contextProvider?: () => LabelContext;
  /** Element that triggers popup show/hide on hover. Defaults to `element`. */
  popupTarget?: GeometryElement;
  /**
   * Reactive slope provider (radians) for auto-positioning labels on lines/curves.
   * When provided and `config.positionIsExplicit` is false, the label position is
   * automatically derived from the element's slope and clamped to the viewport.
   */
  slopeProvider?: () => number;
}

/**
 * Extended label element with update capability.
 */
export interface BoundLabel extends GeometryElement {
  updateText(): void;
  /** Patch config properties and recompute derived styles. Triggers a board update. */
  reconfigure(updates: Partial<ResolvedLabelConfig>): void;
}

/**
 * Bind a label to an element with dynamic updates.
 *
 * @param options - Label binding options
 * @returns The created label element
 */
export function bindLabel(options: BindLabelOptions): BoundLabel {
  const { ctx, themeService, element, contextProvider, popupTarget, slopeProvider } = options;
  const config = { ...options.config };
  const resolveValue = ctx.resolveValue;
  const { board, container } = ctx;

  const theme = themeService.getTheme();

  // Get position functions from the element
  const typedElement = element as unknown as { X: () => number; Y: () => number };

  // Slope-driven positioning: when a slopeProvider is given and the user didn't
  // explicitly choose a position, derive it reactively from the element's slope.
  const useSlopePosition = !!slopeProvider && !config.positionIsExplicit;
  // Intentionally captured once: switching between canvas and element-relative
  // positioning at runtime is not supported (see reconfigure() docs below).
  const isCanvas = isCanvasPosition(config.position);
  const useViewportClamping = !isCanvas;

  // Cache effective position: recompute only when the element moves to a different area
  // or (for slope-driven labels) the slope octant changes.
  let cachedPosition: LabelPosition | undefined;
  let lastAnchorKey = '';

  const getEffectivePosition = (): LabelPosition => {
    if (!useViewportClamping) {
      return config.position;
    }

    if (useSlopePosition) {
      const slope = slopeProvider!();
      const picked = pickPositionFromSlope(slope);
      const octant = LABEL_POSITIONS.indexOf(picked);
      const ax = typedElement.X();
      const ay = typedElement.Y();
      const key = `${octant},${Math.round(ax * 100)},${Math.round(ay * 100)}`;
      if (key !== lastAnchorKey || !cachedPosition) {
        lastAnchorKey = key;
        cachedPosition = clampToViewport(picked, ax, ay, board);
      }
      return cachedPosition;
    }

    const ax = typedElement.X();
    const ay = typedElement.Y();
    const key = `${Math.round(ax * 100)},${Math.round(ay * 100)}`;
    if (key !== lastAnchorKey || !cachedPosition) {
      lastAnchorKey = key;
      cachedPosition = clampToViewport(config.position, ax, ay, board);
    }
    return cachedPosition;
  };

  // Create dynamic text function
  const getText = () => {
    const labelContext = contextProvider?.();
    const raw = formatLabelText(config, labelContext, resolveValue);
    return convertLineBreaks(renderLatex(raw));
  };

  // Pre-computed derived state — render functions read from these `let` variables.
  // reconfigure() recomputes them when config changes, so static labels pay zero overhead.
  let labelStyle = getLabelStyle(themeService, config.variant, config.color);
  let hasBackground = config.variant !== 'tertiary';
  let fontSize = config.fontSize ?? theme.typography.fontSize.label;
  let combinedOffset = getCombinedOffset(config.position, config.offset, useSlopePosition);

  // For clamped labels the offset is computed each frame; for canvas labels it's cached.
  const getOffset = useViewportClamping
    ? () => getCombinedOffset(getEffectivePosition(), config.offset, useSlopePosition)
    : () => combinedOffset;

  const getAnchorAttrs = useViewportClamping
    ? () => getAnchor(getEffectivePosition())
    : () => getAnchor(config.position);

  // Viewport edge correction: measures the label's rendered bounding box and nudges
  // it inward if it extends past the container. Recomputed each frame from the
  // "uncorrected" position so it relaxes to zero when the label is fully inside.
  let viewportCorrectionPx: [number, number] = [0, 0];

  const updateViewportCorrection = () => {
    if (!label) {
      return;
    }
    const rendNode = (label as unknown as { rendNode: HTMLElement }).rendNode;
    if (!rendNode) {
      return;
    }
    const rect = rendNode.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return;
    }
    const cRect = container.getBoundingClientRect();

    // Undo current correction to get the "raw" (uncorrected) screen position.
    // Offset-pixel x maps 1:1 to screen px; offset-pixel y is inverted (positive = up).
    const rawLeft = rect.left - viewportCorrectionPx[0];
    const rawRight = rect.right - viewportCorrectionPx[0];
    const rawTop = rect.top + viewportCorrectionPx[1];
    const rawBottom = rect.bottom + viewportCorrectionPx[1];

    const m = VIEWPORT_INSET_PX;

    // Horizontal: fix right overflow first, then ensure left is still inside.
    let cx = 0;
    if (rawRight > cRect.right - m) {
      cx = -(rawRight - (cRect.right - m));
    }
    if (rawLeft + cx < cRect.left + m) {
      cx = cRect.left + m - rawLeft;
    }

    // Vertical: fix bottom overflow first, then ensure top is still inside.
    let cy = 0;
    if (rawBottom > cRect.bottom - m) {
      cy = rawBottom - (cRect.bottom - m);
    }
    if (rawTop - cy < cRect.top + m) {
      cy = rawTop - (cRect.top + m);
    }

    viewportCorrectionPx = [cx, cy];
  };

  // Declare with `let` so the text function can reference it after creation.
  // During the initial `board.create` call JSXGraph invokes the text function
  // immediately — at that point `label` is still undefined, so the guard below skips
  // the setAttribute. On subsequent updates it's assigned and the anchor is updated.

  // eslint-disable-next-line prefer-const
  let label: GeometryElement | undefined;
  label = board.create(
    'text',
    [
      isCanvas
        ? () => {
            const [bx] = getCanvasBaseCoords(config.position, board);
            return bx + pixelsToCoords(board, container, getOffset()[0], 'x');
          }
        : () => {
            if (useViewportClamping && label) {
              updateViewportCorrection();
            }
            return typedElement.X() + pixelsToCoords(board, container, getOffset()[0] + viewportCorrectionPx[0], 'x');
          },
      isCanvas
        ? () => {
            const [, by] = getCanvasBaseCoords(config.position, board);
            return by + pixelsToCoords(board, container, getOffset()[1], 'y');
          }
        : () => typedElement.Y() + pixelsToCoords(board, container, getOffset()[1] + viewportCorrectionPx[1], 'y'),
      () => {
        const text = getText();
        const hasBreaks = text.includes('<br>');

        // For clamped labels, update anchor alignment reactively and nudge inward
        // if the rendered box breaches the viewport inset.
        // JSXGraph supports anchorX/anchorY at runtime but the TS types are incomplete.
        // Guard: skip during initial board.create when `label` is not yet assigned.
        if (useViewportClamping && label) {
          const anchorAttrs = getAnchorAttrs();
          (label as unknown as { setAttribute: (attrs: { anchorX?: string; anchorY?: string }) => void }).setAttribute(
            anchorAttrs,
          );
        }

        return `<span style="
          ${hasBackground ? `background-color: ${labelStyle.backgroundColor};` : ''}
          color: ${labelStyle.textColor};
          ${hasBackground ? `padding: ${theme.spacing.labelPadding};` : ''}
          ${hasBackground ? 'border-radius: 6px;' : ''}
          font-size: ${fontSize}px;
          font-weight: ${theme.typography.fontWeight.bold};
          font-family: ${theme.typography.fontFamily};
          white-space: ${hasBreaks ? 'pre-line' : 'nowrap'};
          ${config.textAlign ? `text-align: ${config.textAlign};` : ''}
          ${hasBackground ? 'display: inline-block;' : ''}
        ">${text}</span>`;
      },
    ],
    {
      anchorX: getAnchorAttrs().anchorX,
      anchorY: getAnchorAttrs().anchorY,
      parse: false,
      useMathjax: false,
      useMathJax: false,
      cssClass: `jsx-${config.variant}-label-html`,
    },
  ) as GeometryElement;

  // Bootstrap viewport correction: after the first paint the DOM node has valid
  // dimensions, so a single board update computes the correct inset immediately.
  if (useViewportClamping) {
    requestAnimationFrame(() => board.update());
  }

  const boundLabel = label as BoundLabel;

  /**
   * Patch config properties and recompute derived styles. Triggers a board update.
   *
   * **Live via getText()**: `text`, `precision`, `contentVariant`, `formatFn`
   * **Recomputed here**: `variant`, `color`, `fontSize`, `position`, `offset`, `show`, `textAlign`
   * **Not reconfigurable** (baked at creation): `popup`, `anchorAt`, `positionIsExplicit`
   */
  boundLabel.reconfigure = (updates: Partial<ResolvedLabelConfig>) => {
    Object.assign(config, updates);
    labelStyle = getLabelStyle(themeService, config.variant, config.color);
    hasBackground = config.variant !== 'tertiary';
    fontSize = config.fontSize ?? theme.typography.fontSize.label;
    combinedOffset = getCombinedOffset(config.position, config.offset, useSlopePosition);
    cachedPosition = undefined;
    lastAnchorKey = '';
    viewportCorrectionPx = [0, 0];

    // Toggle visibility (unless popup-controlled)
    if (updates.show !== undefined && !config.popup) {
      boundLabel.setAttribute({ visible: config.show });
    }

    board.update();
  };

  // Popup behavior: hide by default, show on hover
  if (config.popup) {
    boundLabel.setAttribute({ visible: false });
    const target = popupTarget ?? element;
    target.on('over', () => boundLabel.setAttribute({ visible: true }));
    target.on('out', () => boundLabel.setAttribute({ visible: false }));
  }

  return boundLabel;
}

/**
 * Create a point label context provider.
 * @param name - Static string or dynamic getter for the label name.
 */
export function createPointLabelContext(name: string | (() => string), point: GeometryElement): () => LabelContext {
  const typedPoint = point as unknown as { X: () => number; Y: () => number };
  const getName = typeof name === 'function' ? name : () => name;
  return () => ({
    type: 'point' as const,
    name: getName(),
    x: () => typedPoint.X(),
    y: () => typedPoint.Y(),
  });
}

/**
 * Create an angle label context provider.
 */
export function createAngleLabelContext(name: string, angle: GeometryElement): () => LabelContext {
  const typedAngle = angle as unknown as { Value: () => number };
  return () => ({
    type: 'angle' as const,
    name,
    radians: () => typedAngle.Value(),
    degrees: () => (typedAngle.Value() * 180) / Math.PI,
  });
}

/**
 * Create a line label context provider.
 * @param name - Static string or dynamic getter for the label name.
 */
export function createLineLabelContext(name: string | (() => string), line: GeometryElement): () => LabelContext {
  const typedLine = line as unknown as { L: () => number };
  const getName = typeof name === 'function' ? name : () => name;
  return () => ({
    type: 'line' as const,
    name: getName(),
    length: () => typedLine.L(),
  });
}

/**
 * Create a polygon label context provider.
 */
export function createPolygonLabelContext(name: string, polygon: GeometryElement): () => LabelContext {
  const typedPolygon = polygon as unknown as { Area: () => number; Perimeter: () => number };
  return () => ({
    type: 'polygon' as const,
    name,
    area: () => typedPolygon.Area(),
    perimeter: () => typedPolygon.Perimeter(),
  });
}

/**
 * Create a curve label context provider.
 * @param name - The curve name
 * @param getExpression - Optional reactive getter for a LaTeX expression string
 */
export function createCurveLabelContext(name: string, getExpression?: () => string): () => LabelContext {
  return () => ({
    type: 'curve' as const,
    name,
    expression: getExpression,
  });
}
